/**
 * Device Debug Utilities
 *
 * These utilities can be accessed via browser console to help troubleshoot
 * device ID and authentication issues (mohamed-said style: localStorage only).
 *
 * Usage in browser console:
 * - window.deviceDebug.showInfo() - Display current device information
 * - window.deviceDebug.clearDevice() - Clear device ID (use with caution)
 */

import { getDeviceId, clearDeviceId } from './deviceId';

const DEVICE_ID_KEY = 'app_device_id';

export const deviceDebug = {
  /**
   * Display current device information
   */
  showInfo: () => {
    const deviceId = getDeviceId();

    const info = {
      deviceId,
      localStorage: {
        [DEVICE_ID_KEY]: localStorage.getItem(DEVICE_ID_KEY),
      },
      browserInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screen: `${screen.width}x${screen.height}`,
        colorDepth: screen.colorDepth,
        timezone: new Date().getTimezoneOffset(),
        hardwareConcurrency: navigator.hardwareConcurrency,
        maxTouchPoints: navigator.maxTouchPoints,
      },
    };

    console.log('=== Device Debug Information ===');
    console.log(JSON.stringify(info, null, 2));
    console.log('================================');

    return info;
  },

  /**
   * Clear device ID from localStorage.
   * WARNING: A new ID will be generated on next API request (e.g. next login).
   */
  clearDevice: () => {
    const confirm = window.confirm(
      'Are you sure you want to clear the device ID?\n\n' +
        'This will:\n' +
        '- Remove the current device ID from localStorage\n' +
        '- Generate a new device ID on next login/API request\n\n' +
        'This may cause device limit errors if you already have 2 devices registered.'
    );

    if (confirm) {
      clearDeviceId();
      console.log('Device ID cleared successfully');
      console.log('A new device ID will be generated on next login');
      return true;
    }

    console.log('Device ID clear cancelled');
    return false;
  },

  /**
   * Get all device-related localStorage keys
   */
  getAllDeviceKeys: () => {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes(DEVICE_ID_KEY)) {
        keys.push(key);
      }
    }

    const data: Record<string, string | null> = {};
    keys.forEach((key) => {
      data[key] = localStorage.getItem(key);
    });

    console.log('=== All Device-Related Keys ===');
    console.log(JSON.stringify(data, null, 2));
    console.log('================================');

    return data;
  },

  /**
   * Display help information
   */
  help: () => {
    console.log(`
=== Device Debug Utilities Help ===

Available commands:

  window.deviceDebug.showInfo()
    - Display current device ID and browser information

  window.deviceDebug.clearDevice()
    - Clear device ID from localStorage (with confirmation)
    - WARNING: Use with caution!

  window.deviceDebug.getAllDeviceKeys()
    - Show all device-related localStorage keys and values

  window.deviceDebug.help()
    - Display this help message

====================================
    `);
  },
};

// Make it available globally for console access
if (typeof window !== 'undefined') {
  (window as any).deviceDebug = deviceDebug;
}
